(function () {
  'use strict';
  if (customElements.get('optic-fit-launcher')) return;

  let controllerPromise;
  const controllerReady = () => window.OpticFit?.version === 1 && window.OpticFit.ownedSessions === true && window.OpticFit.commerce === true;
  function loadController(origin) {
    if (controllerReady()) return Promise.resolve();
    if (!controllerPromise) controllerPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = origin + '/embed.v1.js'; script.async = true;
      const fail = message => { clearTimeout(timer); script.remove(); controllerPromise = null; reject(new Error(message)); };
      const timer = setTimeout(() => fail('Service timeout'), 20000);
      script.onload = () => { clearTimeout(timer); if (controllerReady()) resolve(); else fail('Invalid service controller'); };
      script.onerror = () => fail('Service unavailable');
      document.head.appendChild(script);
    });
    return controllerPromise;
  }

  class OpticFitLauncher extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.request = (this.request || 0) + 1;
      this.button = this.querySelector('[data-optic-fit-open]');
      this.error = this.querySelector('[data-optic-fit-error]');
      if (!this.button) return;
      try { this.variants = JSON.parse(this.querySelector('[data-optic-fit-variants]')?.textContent || '{}'); }
      catch { this.variants = {}; }
      if (!this.variants || typeof this.variants !== 'object' || Array.isArray(this.variants)) this.variants = {};
      this.variantId = this.dataset.variantId || '';
      this.model = this.resolveModel(this.dataset.model);
      this.updateButton();
      this.button.addEventListener('click', () => this.open(), { signal: this.lifecycle.signal });
      this.variantListener = ({ data }) => {
        if (String(data?.sectionId) !== this.dataset.sectionId) return;
        // Invalidate even when two variants resolve to the same model. The selection changed.
        this.close();
        const id = String(data?.variant?.id ?? '');
        this.variantId = id;
        this.model = Object.prototype.hasOwnProperty.call(this.variants, id) ? this.resolveModel(this.variants[id]) : '';
        this.updateButton();
      };
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.variantListener);
      document.addEventListener('shopify:section:unload', event => { if (event.target.contains(this)) this.close(); }, { signal: this.lifecycle.signal });
      this.closest('product-info')?.addEventListener('product-info:loaded', () => {
        this.close(); this.model = this.resolveModel(this.dataset.model); this.updateButton();
      }, { signal: this.lifecycle.signal });
    }

    disconnectedCallback() {
      this.close(); this.lifecycle?.abort(); this.lifecycle = null;
      this.unsubscribe?.(); this.unsubscribe = null;
    }

    resolveModel(value) {
      const variant = typeof value === 'string' ? value.trim() : '';
      const model = variant || (this.dataset.productModel || '').trim();
      // A blank value inherits. "none" explicitly disables, including a product default.
      return model !== 'none' && /^[a-z0-9-]{1,80}$/.test(model) ? model : '';
    }

    updateButton() { this.button.hidden = !this.model; }

    close() {
      this.analyticsSession?.abort(); this.analyticsSession = null;
      this.request = (this.request || 0) + 1;
      const previous = this.session; this.session = null;
      previous?.close();
      if (this.button) this.button.disabled = false;
    }

    trackAnalytics(eventName, variantId = this.variantId, modelId = this.model) {
      document.dispatchEvent(new CustomEvent('vazquier:feature', { detail: {
        event_name: eventName, product_id: this.dataset.productId,
        product_name: this.dataset.productName, variant_id: variantId, model_id: modelId,
      } }));
    }

    observeAnalytics(handle, origin, model, variantId) {
      this.trackAnalytics('try_on_open', variantId, model);
      // Bind to the frame owned by this handle, including its session changes on retry.
      const iframe = [...document.querySelectorAll('.optic-fit-dialog iframe')].find(frame => {
        try { return new URL(frame.src).searchParams.get('session') === handle.session; } catch { return false; }
      });
      if (!iframe) return;
      this.analyticsSession = new AbortController();
      let reported = false;
      window.addEventListener('message', event => {
        const data = event.data;
        if (reported || handle.closed || this.session !== handle || event.origin !== origin || event.source !== iframe.contentWindow ||
            !data || data.channel !== 'optic-fit' || data.version !== 1 || data.session !== handle.session || data.model !== model || data.type !== 'ready') return;
        reported = true;
        this.trackAnalytics('try_on_ready', variantId, model);
      }, { signal: this.analyticsSession.signal });
    }

    storeContext() {
      const source = this.querySelector('[data-optic-fit-commerce]');
      if (!source) return undefined; // Backwards-compatible, single-model launcher.
      const raw = JSON.parse(source.textContent || '{}');
      if (!Array.isArray(raw.choices)) throw new Error('Invalid store catalog');
      const seen = new Set(), choices = [], destinations = new Map();
      // Resolve the active variant first, even in products with many variants.
      const entries = [...raw.choices].sort((a, b) => Number(b?.variantId === this.variantId) - Number(a?.variantId === this.variantId));
      for (const entry of entries) {
        if (!entry || seen.has(entry.variantId) || !/^[a-z0-9-]{1,80}$/.test(entry.modelId || '') || entry.modelId === 'none') continue;
        if (choices.length >= 100) break;
        const url = new URL(entry.url, location.origin);
        if (url.origin !== location.origin || url.username || url.password || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) throw new Error('Invalid product URL');
        if (typeof entry.variantId !== 'string' || !/^\d{1,20}$/.test(entry.variantId)) throw new Error('Invalid variant');
        url.search = ''; url.hash = ''; url.searchParams.set('variant', entry.variantId);
        // Shopify preview links must stay on the same unpublished theme.
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
        seen.add(entry.variantId); destinations.set(entry.variantId, url.href);
        const { variantId, modelId, title, colorLabel, priceLabel, available } = entry;
        choices.push({ variantId, modelId, title, colorLabel, priceLabel, available });
      }
      return { commerce: { selectedVariantId: this.variantId, locale: raw.locale || document.documentElement.lang || 'en', choices }, destinations };
    }

    async open() {
      if (!this.model || !this.lifecycle || !this.isConnected) return;
      this.close();
      const request = ++this.request, lifecycle = this.lifecycle, selectedModel = this.model;
      const current = () => this.isConnected && this.lifecycle === lifecycle && this.request === request;
      if (this.error) this.error.hidden = true;
      this.button.disabled = true;
      try {
        const service = new URL(this.dataset.serviceUrl);
        if (service.username || service.password || (service.protocol !== 'https:' && !(service.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(service.hostname)))) throw new Error('Invalid service');
        const store = this.storeContext();
        await loadController(service.origin);
        if (!current()) return;
        let handle;
        handle = window.OpticFit.open({
          serviceUrl: service.origin, model: selectedModel, trigger: this.button,
          ...(store ? {commerce: store.commerce, onSelect: choice => {
            const destination = store.destinations.get(choice.variantId);
            if (current() && destination) {
              this.trackAnalytics('try_on_select_product', choice.variantId, choice.modelId);
              location.assign(destination);
            }
          }} : {}),
          onClose: () => {
            if (this.session === handle) {
              this.session = null;
              this.analyticsSession?.abort(); this.analyticsSession = null;
            }
          },
        });
        this.session = handle;
        this.observeAnalytics(handle, service.origin, selectedModel, this.variantId);
      } catch {
        if (current() && this.error) { this.error.textContent = this.dataset.errorLabel || 'The fitting room is unavailable. Please try again.'; this.error.hidden = false; }
      } finally {
        if (current()) this.button.disabled = false;
      }
    }
  }
  customElements.define('optic-fit-launcher', OpticFitLauncher);
})();

